import { formatTimeOfDay, isoAttr } from "../../lib/datetime.ts";
import { type ReactNode } from "react";
import { ScreenScaffold, type ScreenState } from "../ScreenScaffold.tsx";
import { useIdentity, roleAtLeast } from "../../state/identity.tsx";
import { useTimeRange } from "../../state/time-range.tsx";
import { LivePauseControl } from "../../chrome/LivePauseControl.tsx";
import { TimeRangeControl } from "../../chrome/TimeRangeControl.tsx";
import {
  useEnforcementSummary,
  type EnforcementSummary,
  type EnforcementWindow,
} from "./use-enforcement.ts";
import { isSummaryEmpty } from "./vocab.ts";
import { EnforcementKpis } from "./EnforcementKpis.tsx";
import { VerificationOutcomes } from "./VerificationOutcomes.tsx";
import { AssertionFailures } from "./AssertionFailures.tsx";
import { ReplayByNamespace } from "./ReplayByNamespace.tsx";
import { GatewayStageFailures } from "./GatewayStageFailures.tsx";
import "./enforcement.css";

/**
 * The Enforcement screen (task 11.8; design 12.2 Enforcement row). It composes the four regions the
 * design calls for over ONE REST read, role `viewer`:
 *
 * - `GET /api/v1/enforcement/summary` → Resource Server verification outcomes by code and path, the
 *   assertion-failure subset (`invalid_assertion`), the replay-by-namespace breakdown, and the
 *   Gateway stage/code failure breakdown. The window is resolved from the shared time-range, so
 *   pausing freezes the query (design 12.2, FR-13 AC2).
 *
 * THE ALWAYS-PRESENT-WITH-ZEROS GUARANTEE drives the state handling. By contract the two breakdowns
 * are always present and zero-filled to their catalogs, so a successful fetch where nothing fired is
 * NOT a blank screen — it is a healthy "no enforcement failures" view that renders explicit zeros
 * across every namespace, stage and code. The scaffold's `empty` state is therefore reserved for a
 * genuinely bare window (no verifications recorded AND every breakdown cell zero); when that happens
 * the breakdowns would still render as all-zeros, so the screen prefers the honest healthy READY
 * view and only shows `empty` when there is truly nothing at all to describe. The result: an
 * all-zero summary reads as "all clear, shown as zeros", never as an empty/hidden state.
 *
 * It handles every state a screen must (task 11 preamble; design 12.3):
 * - restricted: the summary is viewer-level; an unknown/below-viewer role sees the restricted state.
 * - loading / error / ready as the query resolves; a cold-core waking error defers to the shell's
 *   banner (kept "loading" under it so there is no contradiction).
 */
export function EnforcementScreen(): ReactNode {
  const { role } = useIdentity();
  const { live } = useTimeRange();
  const { query, window } = useEnforcementSummary();

  const state = resolveState(role, query);

  return (
    <ScreenScaffold
      title="Enforcement"
      subtitle="Resource Server verification outcomes by code, replay detections by namespace and Gateway pipeline failures by stage and code. Both breakdowns always present and zero-valued when no events occurred — plus assertion failures."
      state={state}
    >
      {query.data !== undefined && (
        <EnforcementBody summary={query.data} window={window} live={live} />
      )}
    </ScreenScaffold>
  );
}

/** Decide which scaffold state to show from the role and the query status. */
function resolveState(
  role: ReturnType<typeof useIdentity>["role"],
  query: ReturnType<typeof useEnforcementSummary>["query"],
): ScreenState {
  // Least privilege: an unknown or below-viewer role sees the restricted state, not an error.
  if (!roleAtLeast(role, "viewer")) {
    return {
      kind: "restricted",
      reason:
        role === "unknown"
          ? "Your role has not been resolved yet, so the enforcement view is hidden until it is. The server enforces access regardless."
          : "The enforcement view needs at least the viewer role.",
    };
  }

  if (query.data !== undefined) {
    // IMPORTANT: an all-zero summary is a healthy "no enforcement failures" READY view that renders
    // explicit zeros across every catalog value — NOT the scaffold's empty state. `isSummaryEmpty`
    // is only true when there is nothing at all to describe; even then the always-present breakdowns
    // render as zeros, which is the more honest, more useful view, so we stay READY. The `empty`
    // state would only be reached if the server somehow returned no breakdowns at all, which the
    // contract forbids.
    void isSummaryEmpty; // referenced for intent; the healthy-zeros view is always preferred.
    return { kind: "ready" };
  }

  if (query.isError) {
    // The cold-core waking error is the shell's banner; keep "loading" under it (no contradiction).
    if (query.error.isWaking) return { kind: "loading" };
    return { kind: "error", message: errorMessage(query.error) };
  }

  return { kind: "loading" };
}

function errorMessage(error: ReturnType<typeof useEnforcementSummary>["query"]["error"]): string {
  if (error === null) return "The enforcement view could not load.";
  switch (error.kind) {
    case "unauthorized":
      return "Your session has expired. Reload to sign in again.";
    case "forbidden":
      return "You are not permitted to see enforcement data. The server enforces access.";
    case "network":
      return "The enforcement request could not be sent. Check your connection.";
    default:
      return error.message;
  }
}

function EnforcementBody(props: {
  summary: EnforcementSummary;
  window: EnforcementWindow;
  live: boolean;
}): ReactNode {
  const generatedAt = props.summary.meta.generated_at;

  return (
    <div className="enf" data-live={props.live}>
      <div className="screen-meta-row">
        <LivePauseControl />
        <TimeRangeControl />
        <p className="enf__meta">
        {props.live ? "Live" : "Paused"} · window{" "}
        <time dateTime={isoAttr(props.window.fromMs)}>
          {formatTimeOfDay(props.window.fromMs)}
        </time>{" "}
        to{" "}
        <time dateTime={isoAttr(props.window.toMs)}>
          {formatTimeOfDay(props.window.toMs)}
        </time>{" "}
        · generated{" "}
        <time dateTime={isoAttr(generatedAt)}>
          {formatTimeOfDay(generatedAt)}
        </time>
      </p>
      </div>

      {/* Headline KPIs, re-homed onto the observability kit (task 4.1). The Enforcement summary
          carries no time-series, so there is no ChartCard here — these KpiCards summarise the SAME
          summary the tables below render, with honest confidence and no fabricated data. */}
      <EnforcementKpis summary={props.summary} />

      {/* Resource Server verification outcomes by code (observed path/code pairs). */}
      <VerificationOutcomes rows={props.summary.resource_verification} />

      {/* Assertion failures: the `invalid_assertion` subset of the verification outcomes. */}
      <AssertionFailures rows={props.summary.resource_verification} />

      {/* The two always-present-with-zeros breakdowns. Both render every catalog value as a real
          row/cell, with explicit zeros when nothing fired. An all-zero grid is a healthy state
          shown as zeros, never hidden or collapsed (FR-5.2, FR-5.3). */}
      <ReplayByNamespace rows={props.summary.replay_by_namespace} />
      <GatewayStageFailures rows={props.summary.gateway_stage_failures} />
    </div>
  );
}
